import React, { useEffect, useRef, useState, createContext, useContext } from "react";
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import * as turf from "@turf/turf";
import "@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css";

const DrawContext = createContext();

export const useDraw = () => {
  const context = useContext(DrawContext);
  if (context === undefined) {
    throw new Error("useDraw must be used within a MapDrawController");
  }
  return context;
};

const MapDrawController = ({ map, children }) => {
  const mapInstance = map || window.layersMapInstance;

  // Get access to the labels map instance
  const labelsMapInstance = window.labelsMapInstance;

  // We'll store the polygon area (in hectares) in React state
  const [polygonArea, setPolygonArea] = useState(0);

  // Whether drawing mode is active
  const [isDrawingMode, setIsDrawingMode] = useState(false);

  const drawRef = useRef(null);
  const drawAddedRef = useRef(false);

  // For debugging purposes
  const logMapInfo = () => {
    console.log("Map instances:", {
      mapInstance: !!mapInstance,
      labelsMapInstance: !!labelsMapInstance,
      windowLabelsMapInstance: !!window.labelsMapInstance,
      drawRef: !!drawRef.current,
      drawAdded: drawAddedRef.current
    });
  };

  // Create and initialize the draw control
  useEffect(() => {
    if (!mapInstance) {
      console.warn("No map instance provided to MapDrawController.");
      return;
    }

    if (!labelsMapInstance) {
      console.warn("No labels map instance available.");
      return;
    }

    logMapInfo();

    // Create a single MapboxDraw instance if not already created
    if (!drawRef.current) {
      console.log("Creating MapboxDraw instance");
      drawRef.current = new MapboxDraw({
        displayControlsDefault: false,
        controls: {
          polygon: true,
          trash: true
        },
        styles: [
          // Polygon fill
          {
            id: 'gl-draw-polygon-fill',
            type: 'fill',
            filter: ['all', ['==', '$type', 'Polygon']],
            paint: {
              'fill-color': '#3b99fc',
              'fill-outline-color': '#3b99fc',
              'fill-opacity': 0.3
            }
          },
          // Polygon outline
          {
            id: 'gl-draw-polygon-stroke-active',
            type: 'line',
            filter: ['all', ['==', '$type', 'Polygon']],
            paint: {
              'line-color': '#3b99fc',
              'line-width': 3
            }
          },
          // Vertex points
          {
            id: 'gl-draw-polygon-and-line-vertex-active',
            type: 'circle',
            filter: ['all', ['==', 'meta', 'vertex']],
            paint: {
              'circle-radius': 5,
              'circle-color': '#fff',
              'circle-stroke-color': '#3b99fc',
              'circle-stroke-width': 2
            }
          }
        ]
      });
    }

    // Add the draw control to the labels map if not already added
    if (!drawAddedRef.current) {
      console.log("Adding MapboxDraw to labels map");

      // Try to remove any existing controls first
      try {
        const existingControls = document.querySelectorAll(".mapbox-gl-draw_ctrl");
        existingControls.forEach(control => {
          control.remove();
        });
      } catch (e) {
        console.log("No existing controls to remove");
      }

      // Make the labels map container interactive when drawing
      const labelsMapContainer = document.getElementById('labelsMap');
      if (labelsMapContainer) {
        // Allow pointer events when drawing is active
        labelsMapContainer.style.pointerEvents = 'auto';
        console.log("Enabled pointer events on labelsMap");
      }

      // Now add our control to the labels map
      try {
        labelsMapInstance.addControl(drawRef.current);
        drawAddedRef.current = true;
        console.log("Successfully added draw control to labels map");
      } catch (err) {
        console.error("Error adding draw control to labels map:", err);
      }

      // *** IMPORTANT: Expose the draw instance on both maps and window
      labelsMapInstance.__draw = drawRef.current;
      mapInstance.__draw = drawRef.current;
      window.mapboxDrawInstance = drawRef.current; // Extra reference that won't be affected by map style changes
      console.log("Set draw references on maps and window");

      // Hide Mapbox's controls immediately and repeatedly
      hideMapboxControls();
      const hideInterval = setInterval(hideMapboxControls, 300);

      // Add event listeners to the labels map
      try {
        labelsMapInstance.on("draw.create", handleDrawCreate);
        labelsMapInstance.on("draw.update", handleDrawUpdate);
        labelsMapInstance.on("draw.delete", handleDrawDelete);
        console.log("Added draw event listeners to labels map");
      } catch (err) {
        console.error("Error adding event listeners:", err);
      }

      // Add event listener for basemap changes
      window.addEventListener('mapBasemapChanged', handleBasemapChange);

      // Store the interval ID for cleanup
      return () => {
        clearInterval(hideInterval);

        // Remove event listener
        window.removeEventListener('mapBasemapChanged', handleBasemapChange);

        // Clean up event listeners if we unmount
        if (drawAddedRef.current && labelsMapInstance) {
          try {
            labelsMapInstance.off("draw.create", handleDrawCreate);
            labelsMapInstance.off("draw.update", handleDrawUpdate);
            labelsMapInstance.off("draw.delete", handleDrawDelete);
          } catch (err) {
            console.warn("Error removing event listeners:", err);
          }
        }

        // Reset the labels map container
        if (labelsMapContainer) {
          labelsMapContainer.style.pointerEvents = 'none';
        }
      };
    }
  }, [mapInstance, labelsMapInstance]);

  // Handle basemap change
  const handleBasemapChange = (event) => {
    console.log("MapDrawController detected basemap change");

    // Set a flag that basemap was changed - the delete function will use this
    window.__basemapWasChanged = true;

    // Make the labels map container interactive
    const labelsMapContainer = document.getElementById('labelsMap');
    if (labelsMapContainer) {
      labelsMapContainer.style.pointerEvents = 'auto';
    }

    // Get saved features if any
    let savedFeatures = [];

    // Try to get features directly from the current draw control
    try {
      if (labelsMapInstance && labelsMapInstance.__draw) {
        const featureCollection = labelsMapInstance.__draw.getAll();
        if (featureCollection && featureCollection.features) {
          savedFeatures = featureCollection.features;
          console.log("Saved features from draw control:", savedFeatures);
        }
      }
    } catch (err) {
      console.warn("Error getting features from draw control:", err);

      // Fall back to saved features if direct access failed
      savedFeatures = window.__savedDrawFeatures || [];
    }

    // Update the saved features in the window object
    window.__savedDrawFeatures = savedFeatures;

    // Create a new draw control
    setTimeout(() => {
      try {
        // Remove any existing draw control first
        if (labelsMapInstance && labelsMapInstance.__draw) {
          try {
            labelsMapInstance.removeControl(labelsMapInstance.__draw);
            console.log("Removed old draw control");
          } catch (err) {
            console.warn("Error removing old draw control:", err);
          }
        }

        // Create a new MapboxDraw control
        const newDrawControl = new MapboxDraw({
          displayControlsDefault: false,
          controls: {
            polygon: true,
            trash: true
          },
          styles: [
            // Polygon fill
            {
              id: 'gl-draw-polygon-fill',
              type: 'fill',
              filter: ['all', ['==', '$type', 'Polygon']],
              paint: {
                'fill-color': '#3b99fc',
                'fill-outline-color': '#3b99fc',
                'fill-opacity': 0.3
              }
            },
            // Polygon outline
            {
              id: 'gl-draw-polygon-stroke-active',
              type: 'line',
              filter: ['all', ['==', '$type', 'Polygon']],
              paint: {
                'line-color': '#3b99fc',
                'line-width': 3
              }
            },
            // Vertex points
            {
              id: 'gl-draw-polygon-and-line-vertex-active',
              type: 'circle',
              filter: ['all', ['==', 'meta', 'vertex']],
              paint: {
                'circle-radius': 5,
                'circle-color': '#fff',
                'circle-stroke-color': '#3b99fc',
                'circle-stroke-width': 2
              }
            }
          ]
        });

        // Add the new draw control to the map
        if (labelsMapInstance) {
          try {
            labelsMapInstance.addControl(newDrawControl);
            console.log("Added new draw control to labels map");

            // Update ALL references consistently
            drawRef.current = newDrawControl;
            labelsMapInstance.__draw = newDrawControl;
            window.labelsMapInstance.__draw = newDrawControl;
            window.mapboxDrawInstance = newDrawControl;
            if (mapInstance) mapInstance.__draw = newDrawControl;

            // Re-add event listeners to the labels map
            labelsMapInstance.on("draw.create", handleDrawCreate);
            labelsMapInstance.on("draw.update", handleDrawUpdate);
            labelsMapInstance.on("draw.delete", handleDrawDelete);
            console.log("Re-added event listeners");

            // Restore saved features if any
            if (savedFeatures && savedFeatures.length > 0) {
              setTimeout(() => {
                try {
                  newDrawControl.add({
                    type: 'FeatureCollection',
                    features: savedFeatures
                  });

                  // Update area state if needed
                  if (savedFeatures[0]) {
                    const areaSqMeters = turf.area(savedFeatures[0]);
                    const areaHectares = areaSqMeters / 10000;
                    setPolygonArea(areaHectares);
                    console.log("Restored features and updated area:", areaHectares);
                  }
                } catch (err) {
                  console.warn("Error restoring features:", err);
                }
              }, 300);
            }
          } catch (err) {
            console.error("Error adding new draw control:", err);
          }
        }
      } catch (err) {
        console.error("Error setting up draw control after basemap change:", err);
      }

      // Always hide the Mapbox controls
      hideMapboxControls();
    }, 300);
  };

  // Hide all Mapbox draw controls
  const hideMapboxControls = () => {
    // Hide all controls with various class names
    const controlSelectors = [
      ".mapbox-gl-draw_ctrl",
      ".mapboxgl-ctrl-top-right",
      ".mapboxgl-ctrl-top-left",
      ".mapbox-gl-draw_point",
      ".mapbox-gl-draw_polygon",
      ".mapbox-gl-draw_trash",
    ];

    controlSelectors.forEach(selector => {
      const elements = document.querySelectorAll(selector);
      elements.forEach(element => {
        if (element) {
          element.style.display = "none";
          element.style.visibility = "hidden";
          element.style.opacity = "0";
          element.style.pointerEvents = "none";
        }
      });
    });

    // Also try to remove any direct Mapbox controls
    try {
      const mapboxControls = document.querySelectorAll(".mapboxgl-ctrl-group");
      mapboxControls.forEach(control => {
        if (control && control.querySelector(".mapbox-gl-draw_point, .mapbox-gl-draw_polygon, .mapbox-gl-draw_trash")) {
          control.style.display = "none";
          control.style.visibility = "hidden";
        }
      });
    } catch (e) {
      console.log("Error hiding mapbox controls:", e);
    }

    // Preserve pointer events if in drawing mode
    if (isDrawingMode) {
      const labelsMapContainer = document.getElementById('labelsMap');
      if (labelsMapContainer) {
        labelsMapContainer.style.pointerEvents = 'auto';
      }
    }
  };

  const handleDrawCreate = (e) => {
    console.log("Draw create event", e);
    try {
      const polygon = e.features[0];
      const areaSqMeters = turf.area(polygon);
      const areaHectares = areaSqMeters / 10000;
      setPolygonArea(areaHectares);
      console.log("Created polygon with area:", areaHectares);

      // Drawing is complete
      setIsDrawingMode(false);

      // Disable pointer events on the labels map when done drawing
      const labelsMapContainer = document.getElementById('labelsMap');
      if (labelsMapContainer) {
        labelsMapContainer.style.pointerEvents = 'none';
      }
    } catch (err) {
      console.error("Error in draw create handler:", err);
    }
  };

  const handleDrawUpdate = (e) => {
    console.log("Draw update event", e);
    try {
      const polygon = e.features[0];
      const areaSqMeters = turf.area(polygon);
      const areaHectares = areaSqMeters / 10000;
      setPolygonArea(areaHectares);
      console.log("Updated polygon with area:", areaHectares);
    } catch (err) {
      console.error("Error in draw update handler:", err);
    }
  };

  const handleDrawDelete = () => {
    console.log("Draw delete event");
    setPolygonArea(0);
  };

  // Called when user clicks "Draw Polygon"
  const enterDrawingMode = () => {
    console.log("Enter drawing mode triggered");
    logMapInfo();

    // First, delete any existing polygons
    try {
      // Find the active draw control
      const activeDrawControl =
        window.mapboxDrawInstance ||
        labelsMapInstance?.__draw ||
        window.labelsMapInstance?.__draw ||
        drawRef.current;

      if (activeDrawControl) {
        // Check if there are any existing features
        try {
          const existingFeatures = activeDrawControl.getAll()?.features || [];
          if (existingFeatures.length > 0) {
            console.log("Clearing existing polygon before drawing new one");
            activeDrawControl.deleteAll();
            setPolygonArea(0);
          }
        } catch (err) {
          console.warn("Error checking for existing features:", err);
        }
      }
    } catch (err) {
      console.warn("Error clearing existing polygons:", err);
    }

    // Now proceed with normal drawing mode initialization

    // Set drawing mode active in state
    setIsDrawingMode(true);
    console.log("Drawing mode set to active");

    // Enable pointer events on the labels map for drawing
    const labelsMapContainer = document.getElementById('labelsMap');
    if (labelsMapContainer) {
      labelsMapContainer.style.pointerEvents = 'auto';
      labelsMapContainer.style.zIndex = '100';
      console.log("Enabled pointer events on labelsMap");
    }

    // Find the active draw control - check all possible references
    const activeDrawControl =
      window.mapboxDrawInstance ||
      labelsMapInstance?.__draw ||
      window.labelsMapInstance?.__draw ||
      drawRef.current;

    if (activeDrawControl) {
      try {
        // Change to polygon drawing mode
        activeDrawControl.changeMode('draw_polygon');
        console.log("Successfully changed to draw_polygon mode");
      } catch (err) {
        console.warn("Error changing to draw_polygon mode:", err);

        // If changing mode fails, try recreating the draw control
        console.log("Attempting to recreate draw control");
        recreateDrawControl();
      }
    } else {
      console.warn("No active draw control found for drawing");

      // If no draw control is found, try to recreate it
      console.log("No draw control found, recreating");
      recreateDrawControl();
    }

    // Always hide the Mapbox controls
    hideMapboxControls();
  };

  // Delete the current polygon(s)
  const deletePolygon = () => {
    console.log("Delete polygon triggered");

    // First, set polygon area to 0 no matter what happens next
    setPolygonArea(0);

    // Reset any saved features in memory
    window.__savedDrawFeatures = null;

    // Find the active draw control - check all possible references
    const activeDrawControl =
      window.mapboxDrawInstance ||
      labelsMapInstance?.__draw ||
      window.labelsMapInstance?.__draw ||
      drawRef.current;

    if (activeDrawControl) {
      try {
        // Try to delete all features
        activeDrawControl.deleteAll();
        console.log("Delete all attempted");
      } catch (err) {
        console.warn("Error in deleteAll:", err);
      }
    } else {
      console.warn("No active draw control found for deletion");
    }

    // This is a critical addition - after a basemap change, we need to force recreation
    // of the draw control to ensure it's properly connected to the map
    if (window.__basemapWasChanged) {
      try {
        // Force recreation of the draw control
        console.log("Recreating draw control after basemap change");

        // First remove any existing draw control
        if (labelsMapInstance) {
          try {
            if (labelsMapInstance.__draw) {
              labelsMapInstance.removeControl(labelsMapInstance.__draw);
            }
          } catch (e) {
            console.warn("Error removing control:", e);
          }

          // Create a new draw control
          const newDrawControl = new MapboxDraw({
            displayControlsDefault: false,
            controls: {
              polygon: true,
              trash: true
            },
            styles: [
              // Polygon fill
              {
                id: 'gl-draw-polygon-fill',
                type: 'fill',
                filter: ['all', ['==', '$type', 'Polygon']],
                paint: {
                  'fill-color': '#3b99fc',
                  'fill-outline-color': '#3b99fc',
                  'fill-opacity': 0.3
                }
              },
              // Polygon outline
              {
                id: 'gl-draw-polygon-stroke-active',
                type: 'line',
                filter: ['all', ['==', '$type', 'Polygon']],
                paint: {
                  'line-color': '#3b99fc',
                  'line-width': 3
                }
              },
              // Vertex points
              {
                id: 'gl-draw-polygon-and-line-vertex-active',
                type: 'circle',
                filter: ['all', ['==', 'meta', 'vertex']],
                paint: {
                  'circle-radius': 5,
                  'circle-color': '#fff',
                  'circle-stroke-color': '#3b99fc',
                  'circle-stroke-width': 2
                }
              }
            ]
          });

          // Add the new control
          labelsMapInstance.addControl(newDrawControl);

          // Update all references
          drawRef.current = newDrawControl;
          labelsMapInstance.__draw = newDrawControl;
          window.labelsMapInstance.__draw = newDrawControl;
          window.mapboxDrawInstance = newDrawControl;
          if (mapInstance) mapInstance.__draw = newDrawControl;

          // Re-add event listeners
          labelsMapInstance.on("draw.create", handleDrawCreate);
          labelsMapInstance.on("draw.update", handleDrawUpdate);
          labelsMapInstance.on("draw.delete", handleDrawDelete);

          // Force a map repaint
          labelsMapInstance.triggerRepaint();

          console.log("Draw control recreated");
        }

        // Reset the flag
        window.__basemapWasChanged = false;
      } catch (err) {
        console.error("Error recreating draw control:", err);
      }
    }

    // Always hide the Mapbox controls
    hideMapboxControls();
  };

  // Helper function to recreate the draw control
  const recreateDrawControl = () => {
    try {
      console.log("Recreating draw control");

      // Create a new MapboxDraw control
      const newDrawControl = new MapboxDraw({
        displayControlsDefault: false,
        controls: {
          polygon: true,
          trash: true
        },
        styles: [
          // Polygon fill (same styles as before)
          {
            id: 'gl-draw-polygon-fill',
            type: 'fill',
            filter: ['all', ['==', '$type', 'Polygon']],
            paint: {
              'fill-color': '#3b99fc',
              'fill-outline-color': '#3b99fc',
              'fill-opacity': 0.3
            }
          },
          // Polygon outline
          {
            id: 'gl-draw-polygon-stroke-active',
            type: 'line',
            filter: ['all', ['==', '$type', 'Polygon']],
            paint: {
              'line-color': '#3b99fc',
              'line-width': 3
            }
          },
          // Vertex points
          {
            id: 'gl-draw-polygon-and-line-vertex-active',
            type: 'circle',
            filter: ['all', ['==', 'meta', 'vertex']],
            paint: {
              'circle-radius': 5,
              'circle-color': '#fff',
              'circle-stroke-color': '#3b99fc',
              'circle-stroke-width': 2
            }
          }
        ]
      });

      // Try to remove any existing draw control
      if (labelsMapInstance && labelsMapInstance.__draw) {
        try {
          labelsMapInstance.removeControl(labelsMapInstance.__draw);
        } catch (err) {
          console.warn("Error removing existing draw control:", err);
        }
      }

      // Add the new control to the labels map
      if (labelsMapInstance) {
        try {
          labelsMapInstance.addControl(newDrawControl);

          // Update all references
          drawRef.current = newDrawControl;
          labelsMapInstance.__draw = newDrawControl;
          window.labelsMapInstance.__draw = newDrawControl;
          window.mapboxDrawInstance = newDrawControl; // Extra reference
          if (mapInstance) mapInstance.__draw = newDrawControl;

          // Re-add event listeners
          labelsMapInstance.on("draw.create", handleDrawCreate);
          labelsMapInstance.on("draw.update", handleDrawUpdate);
          labelsMapInstance.on("draw.delete", handleDrawDelete);

          // Immediately try to change to drawing mode
          try {
            newDrawControl.changeMode('draw_polygon');
          } catch (e) {
            console.warn("Error changing to draw mode after recreation:", e);
          }

          console.log("Successfully recreated draw control");
          return true;
        } catch (err) {
          console.error("Error adding recreated draw control:", err);
        }
      } else {
        console.error("No labels map instance available for recreating draw control");
      }
    } catch (err) {
      console.error("Error recreating draw control:", err);
    }

    return false;
  };

  // Called when user wants to hide the toolbar
  const hideDrawToolbar = () => {
    setIsDrawingMode(false);

    // Disable pointer events on the labels map when hiding toolbar
    const labelsMapContainer = document.getElementById('labelsMap');
    if (labelsMapContainer) {
      labelsMapContainer.style.pointerEvents = 'none';
    }

    // Always hide the Mapbox controls
    hideMapboxControls();
  };

  // Add global CSS to ensure labels map can receive pointer events when in drawing mode
  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.textContent = `
      #labelsMap {
        z-index: 10 !important;
      }
      .drawing-active #labelsMap {
        pointer-events: auto !important;
      }
    `;
    document.head.appendChild(styleElement);

    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  // Update body class when drawing mode changes
  useEffect(() => {
    if (isDrawingMode) {
      document.body.classList.add('drawing-active');
      // Force pointer events on the labels map
      const labelsMapContainer = document.getElementById('labelsMap');
      if (labelsMapContainer) {
        labelsMapContainer.style.pointerEvents = 'auto';
      }
    } else {
      document.body.classList.remove('drawing-active');
    }
  }, [isDrawingMode]);

  return (
    <DrawContext.Provider
      value={{
        enterDrawingMode,
        hideDrawToolbar,
        deletePolygon,
        polygonArea,
        isDrawingMode,
      }}
    >
      {children}
    </DrawContext.Provider>
  );
};

export default MapDrawController;
